import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Response,
  SelectItemProps,
  PermissionEnum,
  Currency,
  Country,
  formatBalance,
} from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import { ApplicationType } from '../../hoc/new-application/types';
import {
  TransactionTypeProps,
  RiskScoreRange,
  ApplicationScheme,
} from '../../hoc/customers/types';
import { clientsDictionariesApi, PermissionReponse } from './services';
import {
  ClientRange,
  ClientRangeTypeEnum,
  RiskScoreItem,
  SicCode,
  Employee,
  PaymentType,
} from './types';

export interface PspAccount {
  id: string;
  description: string;
  pspPartnerId: string;
  pspPartner: {
    id: string;
    companyName: string;
    description?: string;
  };
  account?: {
    clientName: string;
    id: string;
    fullNumber: string;
  };
}

export interface CustomerDictionariesState {
  countries?: SelectItemProps[];
  deliveryChannelsIndividual?: SelectItemProps[];
  deliveryChannelsCorporate?: SelectItemProps[];
  monthlyTurnover?: SelectItemProps[];
  numberOfIncomingTransactions?: SelectItemProps[];
  numberOfOutgoingTransactions?: SelectItemProps[];
  valueOfIncomingTransactions?: SelectItemProps[];
  valueOfOutgoingTransactions?: SelectItemProps[];
  maxValueOfIncomingPayment?: SelectItemProps[];
  maxValueOfOutgoingPayment?: SelectItemProps[];
  amountOfOnePayment?: SelectItemProps[];
  monthlyTurnoverCorporate?: SelectItemProps[];
  numberOfIncomingTransactionsCorporate?: SelectItemProps[];
  numberOfOutgoingTransactionsCorporate?: SelectItemProps[];
  valueOfIncomingTransactionsCorporate?: SelectItemProps[];
  valueOfOutgoingTransactionsCorporate?: SelectItemProps[];
  maxValueOfIncomingPaymentCorporate?: SelectItemProps[];
  maxValueOfOutgoingPaymentCorporate?: SelectItemProps[];
  amountOfOnePaymentCorporate?: SelectItemProps[];
  currencies?: SelectItemProps[];
  currenciesList?: Currency[];
  sicCodes?: SelectItemProps[];
  sicCodesDictionaries?: SelectItemProps[];
  sourcesOfFunds?: SelectItemProps[];
  productsIndividual?: SelectItemProps[];
  productsCorporate?: SelectItemProps[];
  clientsActivities?: SelectItemProps[];
  incorporationDates?: SelectItemProps[];
  activePermissions?: PermissionEnum[];
  customersTransactionTypes?: TransactionTypeProps[];
  riskScoreRanges?: RiskScoreRange[];
  baseCurrency?: Currency;
  dictionariesIsLoading?: boolean;
  infoDictionariesIsLoading?: boolean;
  pspAccounts?: PspAccount[];
  employeesList?: SelectItemProps[];
  employee?: Employee;
  cardsEnabled?: boolean;
  currentEmployee?: Employee;
  accessToken?: string;
  beneficiarySettings?: {
    data: {
      value: boolean;
    };
    key: string;
  }[];
  overdraftEnabled?: boolean;
  applicationSchemes?: ApplicationScheme[];
  paymentTypes?: PaymentType[];
  casp?: CASP[];
  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
}

const initialState: CustomerDictionariesState = {
  dictionariesIsLoading: true,
  infoDictionariesIsLoading: true,
};

const getFromYearLabel = (year: number, array: number[]) => {
  let fromYear = '';

  if (year === 0) {
    fromYear = 'Less then 1 year';
  } else {
    const closestRight = Math.min(...array.filter((number) => number > year));

    const maxNumber = Math.max(...array);

    if (closestRight && year !== maxNumber) {
      fromYear = `More than ${year} year${
        year > 4 ? 's' : ''
      } less than ${closestRight}`;
    } else if (year === maxNumber) {
      fromYear = `More than ${year} year${year > 4 ? 's' : ''}`;
    }
  }

  return fromYear;
};

export const customerDictionariesSlice = createSlice({
  name: 'customerDictionaries',
  initialState,
  reducers: {
    getCustomerDictionaries: () => {},
    getCustomerDictionariesSuccess: (
      state,
      action: PayloadAction<CustomerDictionariesState>,
    ) => {
      state.amountOfOnePayment = action.payload.amountOfOnePayment;
    },
    setCustomersCountries: (state, action: PayloadAction<Country[]>) => {
      state.countries = action.payload?.map((item) => ({
        name: item.name,
        value: item.id,
      }));
    },
    setCustomersCurrencies: (state, action: PayloadAction<Currency[]>) => {
      state.currencies = action.payload?.map((item) => ({
        name: item.description,
        value: item.id,
        suffix: item.name,
      }));

      state.currenciesList = action.payload?.map((item) => item);
    },
    setCustomersBaseCurrency: (state, action: PayloadAction<Currency>) => {
      state.baseCurrency = action.payload;
    },
    setCustomersSettings: (
      state,
      action: PayloadAction<{ key: string; data: { value: boolean } }[]>,
    ) => {
      const cardsSettings = action.payload?.find(
        (item) => item.key === 'CARDS',
      );

      if (cardsSettings) {
        state.cardsEnabled = cardsSettings.data.value;
      }

      const overdraftSetting = action.payload?.find(
        (item) => item.key === 'OVERDRAFT-ENABLED',
      );

      if (overdraftSetting) {
        if (
          overdraftSetting.data.value === undefined ||
          overdraftSetting.data.value === null
        ) {
          state.overdraftEnabled = false;
        } else {
          state.overdraftEnabled = overdraftSetting.data.value;
        }
      }

      state.beneficiarySettings = action.payload
        ?.filter((item) => item.key.includes('BENEFICIARIES'))
        ?.map((item) => item);
    },
    setActivePermissions: (state, action: PayloadAction<PermissionEnum[]>) => {
      state.activePermissions = action.payload;
    },
    setPaymentTypes: (state, action: PayloadAction<PaymentType[]>) => {
      state.paymentTypes = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getClientsBaseCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: Currency }>) => {
        state.baseCurrency = action.payload.data;
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersCountries.matchFulfilled,
      (state, action: PayloadAction<Response<Country>>) => {
        state.countries = action.payload?.data.edges.map((item) => ({
          name: item.node.name,
          value: item.node.id,
        }));
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersCASP.matchFulfilled,
      (state, action: PayloadAction<Response<CASP>>) => {
        state.casp = action.payload?.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersCurrencies.matchFulfilled,
      (state, action: PayloadAction<Response<Currency>>) => {
        state.currencies = action.payload?.data.edges.map((item) => ({
          name: item.node.description,
          value: item.node.id,
          suffix: item.node.name,
        }));

        state.currenciesList = action.payload?.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersSettings.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{ key: string; data: { value: boolean } }>
        >,
      ) => {
        const cardsSettings = action.payload?.data?.edges?.find(
          (item) => item.node.key === 'CARDS',
        );

        if (cardsSettings) {
          state.cardsEnabled = cardsSettings.node.data.value;
        }

        const overdraftSetting = action.payload?.data?.edges?.find(
          (item) => item.node.key === 'OVERDRAFT-ENABLED',
        );

        if (overdraftSetting) {
          if (
            overdraftSetting.node.data.value === undefined ||
            overdraftSetting.node.data.value === null
          ) {
            state.overdraftEnabled = false;
          } else {
            state.overdraftEnabled = overdraftSetting.node.data.value;
          }
        }

        state.beneficiarySettings = action.payload?.data?.edges
          ?.filter((item) => item.node.key.includes('BENEFICIARIES'))
          ?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersSicCodes.matchFulfilled,
      (state, action: PayloadAction<Response<SicCode>>) => {
        state.sicCodes = action.payload.data.edges.map((item) => ({
          name: item.node.code,
          value: item.node.id,
          description: item.node.description,
        }));
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersPaymentTypes.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentType>>) => {
        state.paymentTypes = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomerEmployee.matchFulfilled,
      (state, action: PayloadAction<{ data: Employee }>) => {
        state.currentEmployee = action.payload.data;
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersInfoDictionaries
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{
          ranges: Response<ClientRange>;
          deliveryChannelsIndividual: Response<RiskScoreItem>;
          deliveryChannelsCorporate: Response<RiskScoreItem>;
          sicCodes: Response<SicCode>;
          productsIndividual: Response<RiskScoreItem>;
          productsCorporate: Response<RiskScoreItem>;
          sourcesOfFunds: Response<RiskScoreItem>;
          clientsActivities: Response<RiskScoreItem>;
          transactionTypes: Response<TransactionTypeProps>;
          riskScoreRanges: Response<RiskScoreRange>;
          pspAccounts: Response<PspAccount>;
        }>,
      ) => {
        const {
          ranges,
          deliveryChannelsIndividual,
          deliveryChannelsCorporate,
          productsIndividual,
          productsCorporate,
          sourcesOfFunds,
          clientsActivities,
          sicCodes,
          transactionTypes,
          riskScoreRanges,
          pspAccounts,
        } = action.payload;

        if (riskScoreRanges) {
          state.riskScoreRanges = riskScoreRanges.data.edges
            .map((item) => item.node)
            .sort((a, b) => a.rangeStart - b.rangeStart);
        }

        if (transactionTypes) {
          state.customersTransactionTypes = transactionTypes.data.edges.map(
            (item) => item.node,
          );
        }

        if (sicCodes) {
          state.sicCodesDictionaries = sicCodes.data.edges.map((item) => ({
            name: item.node.code,
            value: item.node.id,
            description: item.node.description,
            suffix: item.node.description,
          }));
        }

        if (deliveryChannelsIndividual) {
          state.deliveryChannelsIndividual =
            deliveryChannelsIndividual.data.edges.map((item) => ({
              name: item.node.name,
              value: item.node.id,
              description: `Risk Score: ${item.node.riskScore}`,
            }));
        }

        if (deliveryChannelsCorporate) {
          state.deliveryChannelsCorporate =
            deliveryChannelsCorporate.data.edges.map((item) => ({
              name: item.node.name,
              value: item.node.id,
              description: `Risk Score: ${item.node.riskScore}`,
            }));
        }

        if (productsIndividual) {
          state.productsIndividual = productsIndividual.data.edges.map(
            (item) => ({
              name: item.node.name,
              value: item.node.id,
              description: `Risk Score: ${item.node.riskScore}`,
            }),
          );
        }

        if (productsCorporate) {
          state.productsCorporate = productsCorporate.data.edges.map(
            (item) => ({
              name: item.node.name,
              value: item.node.id,
              description: `Risk Score: ${item.node.riskScore}`,
            }),
          );
        }

        if (sourcesOfFunds) {
          state.sourcesOfFunds = sourcesOfFunds.data.edges.map((item) => ({
            name: item.node.name,
            value: item.node.id,
            description: `Risk Score: ${item.node.riskScore}`,
          }));
        }

        if (clientsActivities) {
          state.clientsActivities = clientsActivities.data.edges.map(
            (item) => ({
              name: item.node.name,
              value: item.node.id,
              description: `Risk Score: ${item.node.riskScore}`,
            }),
          );
        }

        if (pspAccounts) {
          state.pspAccounts = pspAccounts.data.edges.map((item) => item.node);
        }

        if (ranges) {
          const monthlyTurnover = ranges.data.edges
            .filter(
              (item) =>
                item.node.type === ClientRangeTypeEnum.MONTHLY_TURNOVER &&
                item.node.clientType === ApplicationType.INDIVIDUAL,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          const monthlyTurnoverCorporate = ranges.data.edges
            .filter(
              (item) =>
                item.node.type === ClientRangeTypeEnum.MONTHLY_TURNOVER &&
                item.node.clientType === ApplicationType.CORPORATE,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          state.monthlyTurnover = monthlyTurnover.map((item, index) => ({
            name: `${
              monthlyTurnover[index + 1] ? 'From' : 'Over'
            } ${formatBalance(item.from, state.baseCurrency)}${
              monthlyTurnover[index + 1]
                ? ` to ${formatBalance(
                    monthlyTurnover[index + 1].from,
                    state.baseCurrency,
                  )}`
                : ''
            }`,
            value: item.id,
          }));

          state.monthlyTurnoverCorporate = monthlyTurnoverCorporate.map(
            (item, index) => ({
              name: `${
                monthlyTurnoverCorporate[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                monthlyTurnoverCorporate[index + 1]
                  ? ` to ${formatBalance(
                      monthlyTurnoverCorporate[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }),
          );

          const numberOfIncomingTransactions = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.INDIVIDUAL,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          const numberOfIncomingTransactionsCorporate = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.CORPORATE,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          state.numberOfIncomingTransactions = numberOfIncomingTransactions.map(
            (item, index) => ({
              name: `${
                numberOfIncomingTransactions[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, { decimals: 0 } as Currency, 0)}${
                numberOfIncomingTransactions[index + 1]
                  ? ` to ${formatBalance(
                      numberOfIncomingTransactions[index + 1].from,
                      { decimals: 0 } as Currency,
                      0,
                    )}`
                  : ''
              }`,
              value: item.id,
            }),
          );

          state.numberOfIncomingTransactionsCorporate =
            numberOfIncomingTransactionsCorporate.map((item, index) => ({
              name: `${
                numberOfIncomingTransactionsCorporate[index + 1]
                  ? 'From'
                  : 'Over'
              } ${formatBalance(item.from, { decimals: 0 } as Currency, 0)}${
                numberOfIncomingTransactionsCorporate[index + 1]
                  ? ` to ${formatBalance(
                      numberOfIncomingTransactionsCorporate[index + 1].from,
                      { decimals: 0 } as Currency,
                      0,
                    )}`
                  : ''
              }`,
              value: item.id,
            }));

          const numberOfOutgoingTransactions = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.INDIVIDUAL,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          const numberOfOutgoingTransactionsCorporate = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.CORPORATE,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          state.numberOfOutgoingTransactions = numberOfOutgoingTransactions.map(
            (item, index) => ({
              name: `${
                numberOfOutgoingTransactions[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, { decimals: 0 } as Currency, 0)}${
                numberOfOutgoingTransactions[index + 1]
                  ? ` to ${formatBalance(
                      numberOfOutgoingTransactions[index + 1].from,
                      { decimals: 0 } as Currency,
                      0,
                    )}`
                  : ''
              }`,
              value: item.id,
            }),
          );

          state.numberOfOutgoingTransactionsCorporate =
            numberOfOutgoingTransactionsCorporate.map((item, index) => ({
              name: `${
                numberOfOutgoingTransactionsCorporate[index + 1]
                  ? 'From'
                  : 'Over'
              } ${formatBalance(item.from, { decimals: 0 } as Currency, 0)}${
                numberOfOutgoingTransactionsCorporate[index + 1]
                  ? ` to ${formatBalance(
                      numberOfOutgoingTransactionsCorporate[index + 1].from,
                      { decimals: 0 } as Currency,
                      0,
                    )}`
                  : ''
              }`,
              value: item.id,
            }));

          const valueOfIncomingTransactions = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.INDIVIDUAL,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          const valueOfIncomingTransactionsCorporate = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.CORPORATE,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          state.valueOfIncomingTransactions = valueOfIncomingTransactions.map(
            (item, index) => ({
              name: `${
                valueOfIncomingTransactions[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                valueOfIncomingTransactions[index + 1]
                  ? ` to ${formatBalance(
                      valueOfIncomingTransactions[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }),
          );

          state.valueOfIncomingTransactionsCorporate =
            valueOfIncomingTransactionsCorporate.map((item, index) => ({
              name: `${
                valueOfIncomingTransactionsCorporate[index + 1]
                  ? 'From'
                  : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                valueOfIncomingTransactionsCorporate[index + 1]
                  ? ` to ${formatBalance(
                      valueOfIncomingTransactionsCorporate[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }));

          const valueOfOutgoingTransactions = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.INDIVIDUAL,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          const valueOfOutgoingTransactionsCorporate = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS &&
                item.node.clientType === ApplicationType.CORPORATE,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          state.valueOfOutgoingTransactions = valueOfOutgoingTransactions.map(
            (item, index) => ({
              name: `${
                valueOfOutgoingTransactions[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                valueOfOutgoingTransactions[index + 1]
                  ? ` to ${formatBalance(
                      valueOfOutgoingTransactions[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }),
          );

          state.valueOfOutgoingTransactionsCorporate =
            valueOfOutgoingTransactionsCorporate.map((item, index) => ({
              name: `${
                valueOfOutgoingTransactionsCorporate[index + 1]
                  ? 'From'
                  : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                valueOfOutgoingTransactionsCorporate[index + 1]
                  ? ` to ${formatBalance(
                      valueOfOutgoingTransactionsCorporate[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }));

          const maxValueOfIncomingPayment = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT &&
                item.node.clientType === ApplicationType.INDIVIDUAL,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          const maxValueOfIncomingPaymentCorporate = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT &&
                item.node.clientType === ApplicationType.CORPORATE,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          state.maxValueOfIncomingPayment = maxValueOfIncomingPayment.map(
            (item, index) => ({
              name: `${
                maxValueOfIncomingPayment[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                maxValueOfIncomingPayment[index + 1]
                  ? ` to ${formatBalance(
                      maxValueOfIncomingPayment[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }),
          );

          state.maxValueOfIncomingPaymentCorporate =
            maxValueOfIncomingPaymentCorporate.map((item, index) => ({
              name: `${
                maxValueOfIncomingPaymentCorporate[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                maxValueOfIncomingPaymentCorporate[index + 1]
                  ? ` to ${formatBalance(
                      maxValueOfIncomingPaymentCorporate[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }));

          const maxValueOfOutgoingPayment = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT &&
                item.node.clientType === ApplicationType.INDIVIDUAL,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          const maxValueOfOutgoingPaymentCorporate = ranges.data.edges
            .filter(
              (item) =>
                item.node.type ===
                  ClientRangeTypeEnum.MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT &&
                item.node.clientType === ApplicationType.CORPORATE,
            )
            .map((item) => item.node)
            .sort((a, b) => a.from - b.from);

          state.maxValueOfOutgoingPayment = maxValueOfOutgoingPayment.map(
            (item, index) => ({
              name: `${
                maxValueOfOutgoingPayment[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                maxValueOfOutgoingPayment[index + 1]
                  ? ` to ${formatBalance(
                      maxValueOfOutgoingPayment[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }),
          );

          state.maxValueOfOutgoingPaymentCorporate =
            maxValueOfOutgoingPaymentCorporate.map((item, index) => ({
              name: `${
                maxValueOfOutgoingPaymentCorporate[index + 1] ? 'From' : 'Over'
              } ${formatBalance(item.from, state.baseCurrency)}${
                maxValueOfOutgoingPaymentCorporate[index + 1]
                  ? ` to ${formatBalance(
                      maxValueOfOutgoingPaymentCorporate[index + 1].from,
                      state.baseCurrency,
                    )}`
                  : ''
              }`,
              value: item.id,
            }));

          state.infoDictionariesIsLoading = false;
        }
      },
    );

    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersDictionaries.matchFulfilled,
      (
        state,
        action: PayloadAction<{
          incorporationDates: Response<RiskScoreItem>;
        }>,
      ) => {
        const { incorporationDates } = action.payload;

        if (incorporationDates) {
          const fromYearArray = incorporationDates.data.edges.map(
            (item) => item?.node.fromYear as number,
          );

          state.incorporationDates = incorporationDates.data.edges.map(
            (item) => ({
              name: getFromYearLabel(
                item?.node.fromYear as number,
                fromYearArray,
              ),
              value: item.node.id,
              suffix: item.node.fromYear?.toString(),
            }),
          );
        }

        state.dictionariesIsLoading = false;
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersPermissions.matchFulfilled,
      (state, action: PayloadAction<{ data: PermissionReponse }>) => {
        state.activePermissions = action.payload.data.permissions;

        localStorage.setItem(
          'activePermissions',
          JSON.stringify(action.payload.data.permissions),
        );
      },
    );
    builder.addMatcher(
      clientsDictionariesApi.endpoints.getCustomersApplicationSchemes
        .matchFulfilled,
      (state, action: PayloadAction<Response<ApplicationScheme>>) => {
        state.applicationSchemes = action.payload.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder
      .addMatcher(
        clientsDictionariesApi.endpoints.getCustomersEmployeesList
          .matchFulfilled,
        (state, action: PayloadAction<Response<Employee>>) => {
          const formattedEmployees = action.payload.data.edges
            .filter((item) => item.node.status === 'ACTIVE')
            .map((item) => item.node)
            .map((item) => ({
              ...item,
              permissions: item?.employeePermissions
                ? item?.employeePermissions[0]?.permissions
                : [],
            }))
            .filter(
              (item) =>
                item?.permissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) || !item.permissions.length,
            )
            .map((item) => ({
              name: `${item?.profile?.firstName} ${item?.profile?.lastName}`,
              value: item.id,
            }));

          state.employeesList = formattedEmployees;
        },
      )
      .addMatcher(
        clientsDictionariesApi.endpoints.getClientsWhitelabel.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      );
  },
});

export const {
  getCustomerDictionaries,
  getCustomerDictionariesSuccess,
  setActivePermissions,
  setPaymentTypes,
  setCustomersBaseCurrency,
  setCustomersCountries,
  setCustomersCurrencies,
  setCustomersSettings,
} = customerDictionariesSlice.actions;

export default customerDictionariesSlice.reducer;
